import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCitationMarker, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraAudienceChip, SpecraEmptyState, SpecraInlineBanner, SpecraKeyValueRow, SpecraReferenceRow, SpecraSectionHeader, SpecraSourceCard, SpecraTooltip, SpecraUnverifiedClaim } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useResponseWorkspace } from './useOfficeWorkspace';
import { useInsertSource, useWorkspaceSources } from './useWorkspaceSources';
const KIND_LABEL = {
    past_performance: 'Past performance',
    past_answer: 'Past answer',
    rfi_answer: 'RFI answer',
    touchpoint: 'Customer said',
    library_release: 'Library',
    hearsay: 'Hearsay'
};
// lesson_learned is deferred: never rendered.
const HIDDEN_KINDS = new Set([
    'lesson_learned'
]);
export function insertErrorText(err) {
    if (err instanceof ConnectError) {
        if (err.code === Code.Aborted) return 'Someone saved a newer version. Reload and try again.';
        if (err.code === Code.FailedPrecondition && err.rawMessage) return err.rawMessage;
        if (err.code === Code.NotFound) return 'That source is no longer available to you.';
    }
    return 'The source could not be inserted.';
}
const AUDIENCE_LEVELS = [
    'personal',
    'team',
    'people',
    'everyone'
];
const audienceLevel = (s)=>AUDIENCE_LEVELS.find((l)=>l === s);
const REQ_STATUS = {
    addressed: {
        label: 'Addressed',
        variant: 'success'
    },
    partial: {
        label: 'Partial',
        variant: 'warning'
    },
    missing: {
        label: 'Missing',
        variant: 'danger'
    }
};
function budgetValue(words, pages) {
    return {
        words: words > 0 ? words.toLocaleString() : null,
        pages: pages > 0 ? String(pages) : null
    };
}
/**
 * The Sources tab (WRT-10): what this section must answer, what to draw from,
 * unverified hearsay, and what the document already cites.
 */ export function SourcesRail({ baseVersionId, canWrite, container, documentId, onAskAssist, onInserted, onJumpToAnchor, sectionAnchor, unitNoun }) {
    const sources = useWorkspaceSources(container, documentId, sectionAnchor);
    const workspace = useResponseWorkspace(container, documentId);
    const insert = useInsertSource(container);
    const [pendingRef, setPendingRef] = useState(null);
    const [failed, setFailed] = useState(null);
    const run = (src, mode)=>{
        if (!sectionAnchor || insert.isPending) return;
        setPendingRef(src.ref);
        setFailed(null);
        insert.mutate({
            documentId,
            sectionAnchor,
            sourceRef: src.ref,
            baseVersionId,
            mode
        }, {
            onSuccess: (res)=>onInserted(res.version?.id ?? baseVersionId),
            onError: (err)=>setFailed({
                    ref: src.ref,
                    text: insertErrorText(err)
                }),
            onSettled: ()=>setPendingRef(null)
        });
    };
    const citations = workspace.data?.citations ?? [];
    const data = sources.data;
    const visible = (data?.drawFrom ?? []).filter((s)=>!HIDDEN_KINDS.has(s.kind));
    const hearsay = visible.filter((s)=>s.eligibility === 'hearsay');
    const drawFrom = visible.filter((s)=>s.eligibility !== 'hearsay');
    const { words, pages } = budgetValue(data?.wordLimit ?? 0, data?.pageLimit ?? 0);
    const busy = insert.isPending;
    const inserting = (src)=>busy && pendingRef === src.ref;
    const errorFor = (src)=>failed?.ref === src.ref ? /*#__PURE__*/ _jsx(SpecraText, {
            as: "div",
            role: "alert",
            size: "meta",
            tone: "danger",
            children: failed.text
        }) : null;
    const askAssist = (prefill)=>onAskAssist ? /*#__PURE__*/ _jsx(SpecraButton, {
            "data-testid": Testids.officeWorkspace.sourceAskAssist,
            onClick: ()=>onAskAssist(prefill),
            size: "xs",
            variant: "secondary",
            children: "Ask Assist to use it"
        }) : null;
    let body;
    if (!sectionAnchor) {
        body = /*#__PURE__*/ _jsx(SpecraEmptyState, {
            "data-testid": Testids.officeWorkspace.sourcesEmpty,
            description: "Pick a question to see sources for it.",
            size: "compact",
            title: "No section selected"
        });
    } else if (sources.isLoading) {
        body = /*#__PURE__*/ _jsx(SpecraSpinner, {
            className: "m-[var(--space-4)]",
            label: "Loading sources"
        });
    } else if (sources.isError || !data) {
        body = /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            action: /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>void sources.refetch(),
                size: "sm",
                variant: "secondary",
                children: "Retry"
            }),
            "data-testid": Testids.officeWorkspace.sourcesError,
            tone: "danger",
            children: "Sources could not be loaded."
        });
    } else {
        body = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                data.unavailableKinds.length > 0 ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void sources.refetch(),
                        size: "sm",
                        variant: "secondary",
                        children: "Retry"
                    }),
                    "data-testid": Testids.officeWorkspace.sourcesError,
                    tone: "neutral",
                    children: `Some sources could not load: ${data.unavailableKinds.map((k)=>KIND_LABEL[k] ?? k).join(', ')}.`
                }) : null,
                data.requirements.length > 0 ? /*#__PURE__*/ _jsxs("section", {
                    className: "flex flex-col",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            title: "Requirements this section must answer",
                            variant: "group"
                        }),
                        data.requirements.map((r)=>{
                            const st = REQ_STATUS[r.status];
                            return /*#__PURE__*/ _jsx(SpecraReferenceRow, {
                                "data-testid": Testids.officeWorkspace.requirementRow,
                                label: r.text,
                                reference: r.requirementId,
                                status: st ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: st.variant,
                                    children: st.label
                                }) : undefined
                            }, r.requirementId);
                        })
                    ]
                }) : null,
                words || pages ? /*#__PURE__*/ _jsxs("section", {
                    className: "flex flex-col",
                    children: [
                        words ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Word limit",
                            value: words
                        }) : null,
                        pages ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Page budget",
                            value: pages
                        }) : null
                    ]
                }) : null,
                /*#__PURE__*/ _jsxs("section", {
                    className: "flex flex-col gap-[var(--space-2)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            title: "Draw from",
                            variant: "group"
                        }),
                        drawFrom.length === 0 && hearsay.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            "data-testid": Testids.officeWorkspace.sourcesEmpty,
                            description: "Past answers, proof and library items that fit will show up here.",
                            size: "compact",
                            title: `No sources match this ${unitNoun} yet.`
                        }) : null,
                        drawFrom.map((src)=>{
                            const paraphraseOnly = src.eligibility === 'paraphrase_only';
                            return /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col gap-[var(--space-1)]",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraSourceCard, {
                                        actions: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                !paraphraseOnly && canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                    "data-testid": Testids.officeWorkspace.sourceInsert,
                                                    disabled: busy,
                                                    onClick: ()=>run(src, 'insert'),
                                                    size: "xs",
                                                    variant: "secondary",
                                                    children: inserting(src) ? 'Inserting…' : 'Insert'
                                                }) : null,
                                                askAssist(`Use “${src.title}” in this ${unitNoun}.`)
                                            ]
                                        }),
                                        "data-testid": Testids.officeWorkspace.sourceCard,
                                        description: src.detail || undefined,
                                        kind: /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: "neutral",
                                            children: KIND_LABEL[src.kind] ?? src.kind
                                        }),
                                        meta: paraphraseOnly ? 'Assist can paraphrase this; it is never quoted' : undefined,
                                        title: src.similarityPct > 0 ? `${src.title} (${src.similarityPct}% similar)` : src.title
                                    }),
                                    errorFor(src)
                                ]
                            }, src.ref);
                        })
                    ]
                }),
                hearsay.length > 0 ? /*#__PURE__*/ _jsxs("section", {
                    className: "flex flex-col gap-[var(--space-2)]",
                    "data-testid": Testids.officeWorkspace.sourcesHearsay,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            title: "Unverified hearsay",
                            variant: "group"
                        }),
                        hearsay.map((src)=>{
                            const level = audienceLevel(src.audienceKind);
                            return /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col gap-[var(--space-1)]",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraUnverifiedClaim, {
                                        actions: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                level && src.audienceLabel ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                                                    content: "Who can see this claim. Change it on the claim in Registries.",
                                                    children: /*#__PURE__*/ _jsx(SpecraAudienceChip, {
                                                        level: level,
                                                        readOnly: true,
                                                        size: "xs",
                                                        summary: src.audienceLabel
                                                    })
                                                }) : null,
                                                canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                    "data-testid": Testids.officeWorkspace.sourceCiteHearsay,
                                                    disabled: busy,
                                                    onClick: ()=>run(src, 'cite_hearsay'),
                                                    size: "xs",
                                                    variant: "secondary",
                                                    children: inserting(src) ? 'Inserting…' : 'Cite as hearsay'
                                                }) : null,
                                                askAssist(`Use the unverified hearsay from ${src.byline || 'a contact'} to inform this ${unitNoun}. Paraphrase it; don't quote it.`)
                                            ]
                                        }),
                                        attribution: [
                                            src.byline,
                                            src.occurredOn
                                        ].filter(Boolean).join(', ') || undefined,
                                        claim: src.title
                                    }),
                                    errorFor(src)
                                ]
                            }, src.ref);
                        })
                    ]
                }) : null
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-4)] p-[var(--space-3)]",
        "data-testid": Testids.officeWorkspace.sourcesRail,
        children: [
            body,
            citations.length > 0 ? /*#__PURE__*/ _jsxs("section", {
                className: "flex flex-col",
                "data-testid": Testids.officeWorkspace.citedList,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        title: "Cited in this document",
                        variant: "group"
                    }),
                    citations.map((c)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex items-start gap-[var(--space-2)] py-[var(--space-1-5)]",
                            "data-testid": Testids.officeWorkspace.citedRow,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraCitationMarker, {
                                    index: c.index,
                                    label: `Source ${c.index}: ${c.label}`,
                                    onClick: c.anchor && onJumpToAnchor ? ()=>onJumpToAnchor(c.anchor) : undefined
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex min-w-0 flex-1 flex-col gap-[var(--space-0-5)]",
                                    children: [
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "div",
                                            size: "body-ui",
                                            children: [
                                                c.label,
                                                c.isHearsay ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        ' ',
                                                        /*#__PURE__*/ _jsx(SpecraBadge, {
                                                            variant: "warning",
                                                            children: "Unverified hearsay"
                                                        })
                                                    ]
                                                }) : null
                                            ]
                                        }),
                                        c.sectionTitle ? /*#__PURE__*/ _jsx(SpecraText, {
                                            as: "div",
                                            size: "meta",
                                            tone: "muted",
                                            children: `in ${c.sectionTitle}`
                                        }) : null
                                    ]
                                })
                            ]
                        }, `${c.index}:${c.sourceRef}`))
                ]
            }) : null
        ]
    });
}
